import { PSEUDO_ID_COOKIE } from "@/platform/api/constants"
import { datadog } from "@/platform/observability/datadog/facade"
import { getSessionCookie } from "@/platform/session/auth/get-session-cookie-client"

import { buildMetaCustomData } from "./custom-data"
import { resolveMetaEventId } from "./event-ids"
import { getMetaEventMapEntry } from "./event-map"
import { getMetaAuthenticatedUserUuid } from "./user-identity"

import type { MetaGtmFields, MetaMappedEvent, MetaTrackParams } from "./types"

function trackMissingMetaConversionContext(event: MetaMappedEvent, params: MetaTrackParams): void {
  const mapEntry = getMetaEventMapEntry(event)
  if (!mapEntry?.requiresExplicitEventId || params.metaEventId || params.conversionId) {
    return
  }

  datadog.addAction("meta_conversion_context_missing", {
    event,
    meta_event_name: mapEntry.metaEventName,
    pixel_key: params.conversionSurface || "marketplace",
  })
}

export function buildMetaGtmFields(event: string, params: Record<string, unknown> & MetaTrackParams): MetaGtmFields | undefined {
  const mapEntry = getMetaEventMapEntry(event)
  if (!mapEntry) {
    return undefined
  }

  const metaEventId = resolveMetaEventId(event as MetaMappedEvent, params)
  if (!metaEventId || (mapEntry.requiresExplicitEventId && !params.metaEventId && !params.conversionId)) {
    trackMissingMetaConversionContext(event as MetaMappedEvent, params)
    return undefined
  }

  const customData = buildMetaCustomData(event as MetaMappedEvent, params)
  if (!customData) {
    return undefined
  }

  const externalIds = Array.from(new Set([getMetaAuthenticatedUserUuid(), getSessionCookie(PSEUDO_ID_COOKIE)].filter((id): id is string => !!id)))
  const userData = externalIds.length > 0 ? { external_id: externalIds } : undefined

  return {
    meta: {
      event_name: mapEntry.metaEventName,
      event_id: metaEventId,
      pixel_key: params.conversionSurface || "marketplace",
      custom_data: customData,
      user_data: userData,
    },
  }
}
